/* ----------------------------------------------------------------------------
 * Marketing-A (W3b) data sources — ONE mock/real gate per surface,
 * self-contained and owned end-to-end by Agent MKT-A-FE (kept out of the shared
 * `data/sources.ts` so W3b/W3a stay file-disjoint).
 *
 * Each fetcher is, exactly like the rest of the app:
 *
 *     USE_MOCK_DATA ? mock : real fetch via the api client
 *
 * Real mode targets the LOCKED W3b contract (docs/brain/architecture/
 * w3b-contracts.md §"Locked API contract"): the new `marketing` route group.
 *
 * SAFETY INVARIANTS (W3b):
 *  - The ONLY send path is POST /marketing/campaigns/{id}/send-sandbox. It is
 *    SANDBOX: allowlist + suppression enforced, DRY EMAIL_SENT events, NO real
 *    email. There is deliberately NO real-send fetcher in this module.
 *  - Reads (campaigns / segments / preview / attribution) are workspace-scoped
 *    server-side and never charge or send.
 *  - Adapters are honest by construction: a value the backend cannot attribute
 *    stays null (numOrNull never fabricates a zero); real counted metrics use
 *    intOf (0 is genuine).
 *
 * Mocks are STRICTLY behind USE_MOCK_DATA — never served as real data. In mock
 * mode, create actions append to module-local arrays so the build → list →
 * preview → attribution flows are exercised end-to-end without a backend.
 * -------------------------------------------------------------------------- */

import { api, USE_MOCK_DATA } from '@/lib/api';
import type {
  Campaign,
  CampaignAttribution,
  CampaignCreateInput,
  CampaignList,
  CampaignStatus,
  SandboxSendResult,
  Segment,
  SegmentContact,
  SegmentCreateInput,
  SegmentFilter,
  SegmentFilterOp,
  SegmentList,
  SegmentPreview,
} from '@/types/marketinga';

/* ============================================================================
 * Null-honest coercion — absent / blank / non-numeric all stay null (never a
 * fabricated zero). Distinct from `Number(x) || 0`, which is only correct where
 * 0 is a real, counted metric (funnel counts).
 * ========================================================================== */
function numOrNull(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Real, counted integer — 0 is a genuine value here (an empty funnel stage). */
function intOf(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : 0;
}

function strOf(v: unknown, fallback = ''): string {
  return typeof v === 'string' && v !== '' ? v : fallback;
}

function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v !== '' ? v : null;
}

/* ============================================================================
 * API DTOs — the real response shapes (locked W3b marketing envelopes).
 * ========================================================================== */
interface CampaignDto {
  id?: string | number | null;
  name?: string | null;
  template_id?: string | number | null;
  segment_id?: string | number | null;
  schedule?: string | null;
  status?: string | null;
  audience_count?: number | string | null;
  created_at?: string | null;
  source?: string | null;
}

interface CampaignListDto {
  campaigns?: CampaignDto[];
  source?: string;
}

interface SandboxSendDto {
  dispatched?: number | string | null;
  skipped_suppressed?: number | string | null;
  blocked_not_allowlisted?: number | string | null;
  audience_total?: number | string | null;
  truncated?: boolean | null;
  source?: string | null;
}

interface SegmentFilterDto {
  field?: string | null;
  op?: string | null;
  value?: string | number | null;
}

interface SegmentDto {
  id?: string | number | null;
  name?: string | null;
  filters?: SegmentFilterDto[] | null;
  count?: number | string | null;
  created_at?: string | null;
  source?: string | null;
}

interface SegmentListDto {
  segments?: SegmentDto[];
  source?: string;
}

interface SegmentContactDto {
  id?: string | number | null;
  name?: string | null;
  business_name?: string | null;
  email?: string | null;
  detail?: string | null;
}

interface SegmentPreviewDto {
  contacts?: SegmentContactDto[];
  count?: number | string | null;
  source?: string | null;
}

interface CampaignAttributionDto {
  sent?: number | string | null;
  opened?: number | string | null;
  clicked?: number | string | null;
  replied?: number | string | null;
  opportunities?: number | string | null;
  won_value?: number | string | null;
  currency?: string | null;
  revenue_per_recipient?: number | string | null;
  note?: string | null;
  source?: string | null;
}

/* ============================================================================
 * Adapters — real API shapes → the UI types. Null-honest by construction.
 * ========================================================================== */
const VALID_STATUSES: CampaignStatus[] = ['draft', 'scheduled', 'sending', 'sent', 'paused'];
function adaptStatus(raw: unknown): CampaignStatus {
  const low = typeof raw === 'string' ? raw.toLowerCase() : '';
  return (VALID_STATUSES as string[]).includes(low) ? (low as CampaignStatus) : 'draft';
}

function adaptCampaign(dto: CampaignDto): Campaign {
  return {
    id: String(dto.id ?? ''),
    name: strOf(dto.name, 'Untitled campaign'),
    template_id: dto.template_id === null || dto.template_id === undefined ? null : String(dto.template_id),
    segment_id: dto.segment_id === null || dto.segment_id === undefined ? null : String(dto.segment_id),
    schedule: strOrNull(dto.schedule),
    status: adaptStatus(dto.status),
    // Null-honest: audience is null until the segment is actually counted.
    audience_count: numOrNull(dto.audience_count),
    created_at: strOrNull(dto.created_at),
    source: strOf(dto.source, 'bc_campaigns'),
  };
}

const VALID_OPS: SegmentFilterOp[] = ['eq', 'neq', 'contains', 'exists', 'not_exists'];
function adaptOp(raw: unknown): SegmentFilterOp {
  const low = typeof raw === 'string' ? raw.toLowerCase() : '';
  return (VALID_OPS as string[]).includes(low) ? (low as SegmentFilterOp) : 'eq';
}

function adaptFilter(dto: SegmentFilterDto): SegmentFilter {
  return {
    field: strOf(dto.field, ''),
    op: adaptOp(dto.op),
    value: dto.value === null || dto.value === undefined ? '' : String(dto.value),
  };
}

function adaptSegment(dto: SegmentDto): Segment {
  return {
    id: String(dto.id ?? ''),
    name: strOf(dto.name, 'Untitled segment'),
    filters: (dto.filters ?? []).map(adaptFilter),
    // Null-honest: count is null until a preview has actually been run.
    count: numOrNull(dto.count),
    created_at: strOrNull(dto.created_at),
    source: strOf(dto.source, 'bc_contacts+filters'),
  };
}

function adaptContact(dto: SegmentContactDto): SegmentContact {
  return {
    id: String(dto.id ?? ''),
    name: strOf(dto.name, strOf(dto.business_name, 'Unknown contact')),
    email: strOf(dto.email),
    detail: strOf(dto.detail),
  };
}

/* ============================================================================
 * MOCKS — dev-only, STRICTLY behind USE_MOCK_DATA. Never served as real data.
 * Deliberately include honest-null / honest-empty cases so the empty + null
 * states are exercised in mock mode too. Create actions mutate these local
 * arrays so the build → list → preview → attribution flows work offline.
 * ========================================================================== */
const MOCK_SEGMENTS: Segment[] = [
  {
    id: 'seg_studios_wales',
    name: 'Wales studios — no reply yet',
    filters: [
      { field: 'region', op: 'eq', value: 'Wales' },
      { field: 'last_reply_at', op: 'not_exists', value: '' },
    ],
    count: 61,
    created_at: '2026-07-10T09:00:00Z',
    source: 'bc_contacts+filters',
  },
  {
    id: 'seg_pilates_all',
    name: 'All pilates contacts',
    filters: [{ field: 'category', op: 'contains', value: 'pilates' }],
    // Honest-null: never previewed yet → count stays null, not a fabricated 0.
    count: null,
    created_at: '2026-07-12T14:30:00Z',
    source: 'bc_contacts+filters',
  },
];

const MOCK_CAMPAIGNS: Campaign[] = [
  {
    id: 'cmp_wales_q3',
    name: 'Wales pilates — Q3 intro',
    template_id: 'tpl_intro_v3',
    segment_id: 'seg_studios_wales',
    schedule: null,
    status: 'draft',
    audience_count: 61,
    created_at: '2026-07-13T08:00:00Z',
    source: 'bc_campaigns',
  },
  {
    id: 'cmp_pilates_nl',
    name: 'Pilates newsletter — welcome',
    template_id: 'tpl_welcome_a',
    segment_id: 'seg_pilates_all',
    schedule: '2026-07-20T06:00:00Z',
    status: 'scheduled',
    // Honest-null: audience not counted yet.
    audience_count: null,
    created_at: '2026-07-14T11:15:00Z',
    source: 'bc_campaigns',
  },
  {
    id: 'cmp_studio_launch',
    name: 'Studio launch — full list',
    template_id: 'tpl_launch_v1',
    segment_id: 'seg_studios_wales',
    schedule: null,
    status: 'draft',
    // Large audience: exercises the honest sandbox truncation disclosure
    // (dispatch ceiling reached → truncated=true, audience_total shown).
    audience_count: 1240,
    created_at: '2026-07-15T09:30:00Z',
    source: 'bc_campaigns',
  },
];

const MOCK_PREVIEW_CONTACTS: SegmentContact[] = [
  { id: 'c_1042', name: 'Cardiff Pilates Studio', email: 'hello@cardiffpilates.example', detail: 'Wales · studio' },
  { id: 'c_1039', name: 'Riverside Yoga', email: 'team@riversideyoga.example', detail: 'Wales · yoga' },
  { id: 'c_1028', name: 'Newport Sports Therapy', email: 'admin@newportsports.example', detail: 'Wales · therapy' },
];

const MOCK_ATTRIBUTION: Record<string, CampaignAttribution> = {
  cmp_wales_q3: {
    sent: 61,
    opened: 34,
    clicked: 11,
    replied: 4,
    opportunities: 2,
    won_value: 1980,
    currency: 'GBP',
    revenue_per_recipient: 32.46,
    note: 'attribution joins campaign sends → opportunities; revenue null until a Won opportunity',
    source: 'bksesh_sales_lead_events+bc_crm_opportunities',
  },
  cmp_pilates_nl: {
    sent: 0,
    opened: 0,
    clicked: 0,
    replied: 0,
    opportunities: 0,
    // Honest-null: scheduled, nothing Won → revenue stays null, never £0.
    won_value: null,
    currency: 'GBP',
    revenue_per_recipient: null,
    note: 'attribution joins campaign sends → opportunities; revenue null until a Won opportunity',
    source: 'bksesh_sales_lead_events+bc_crm_opportunities',
  },
};

const ATTRIBUTION_NOTE =
  'attribution joins campaign sends → opportunities; revenue null until a booking/opportunity is Won';

let mockSeq = 1000;
function nextMockId(prefix: string): string {
  mockSeq += 1;
  return `${prefix}_${mockSeq}`;
}

/* ============================================================================
 * CAMPAIGNS — list (free read), create (write), send-sandbox (SANDBOX ONLY).
 * ========================================================================== */

// ---- GET /marketing/campaigns (free read, workspace-scoped) ----------------
export async function fetchCampaigns(): Promise<CampaignList> {
  if (USE_MOCK_DATA) return { campaigns: [...MOCK_CAMPAIGNS], source: 'bc_campaigns' };
  const res = await api.get<CampaignListDto>('/marketing/campaigns');
  return {
    campaigns: (res.campaigns ?? []).map(adaptCampaign),
    source: strOf(res.source, 'bc_campaigns'),
  };
}

// ---- POST /marketing/campaigns (create; no send) ---------------------------
export async function createCampaign(input: CampaignCreateInput): Promise<Campaign> {
  if (USE_MOCK_DATA) {
    const created: Campaign = {
      id: nextMockId('cmp'),
      name: input.name,
      template_id: input.template_id || null,
      segment_id: input.segment_id || null,
      schedule: input.schedule,
      status: input.schedule ? 'scheduled' : 'draft',
      audience_count: MOCK_SEGMENTS.find((s) => s.id === input.segment_id)?.count ?? null,
      created_at: new Date().toISOString(),
      source: 'bc_campaigns',
    };
    MOCK_CAMPAIGNS.unshift(created);
    return created;
  }
  const res = await api.post<CampaignDto>('/marketing/campaigns', input);
  return adaptCampaign(res);
}

// ---- POST /marketing/campaigns/{id}/send-sandbox (SANDBOX ONLY, no real send)
export async function sendCampaignSandbox(campaignId: string): Promise<SandboxSendResult> {
  if (USE_MOCK_DATA) {
    // Deterministic-ish mock: some suppressed, some not allowlisted, rest dry.
    const audience = MOCK_CAMPAIGNS.find((c) => c.id === campaignId)?.audience_count ?? 12;
    const skipped_suppressed = Math.min(2, audience);
    const blocked_not_allowlisted = Math.max(0, Math.min(3, audience - skipped_suppressed));
    // Mirror the backend sandbox ceiling (MAX_DRY_DISPATCH) so the honest
    // truncation-disclosure path is exercised in mock mode too. audience_total
    // is the TRUE segment size; truncated is set only when the ceiling is hit.
    const MAX_DRY_DISPATCH = 500;
    const wouldDispatch = Math.max(0, audience - skipped_suppressed - blocked_not_allowlisted);
    const dispatched = Math.min(wouldDispatch, MAX_DRY_DISPATCH);
    const truncated = wouldDispatch > MAX_DRY_DISPATCH;
    return {
      dispatched,
      skipped_suppressed,
      blocked_not_allowlisted,
      audience_total: audience,
      truncated,
      source: 'worker.sandbox',
    };
  }
  const res = await api.post<SandboxSendDto>(`/marketing/campaigns/${campaignId}/send-sandbox`);
  return {
    dispatched: intOf(res.dispatched),
    skipped_suppressed: intOf(res.skipped_suppressed),
    blocked_not_allowlisted: intOf(res.blocked_not_allowlisted),
    // D-3 honest disclosure — surfaced only when the backend sends them
    // (back-compatible): the TRUE segment size, and whether the sandbox
    // dispatch ceiling truncated the preview. numOrNull never fabricates a 0;
    // truncated is true only when the backend explicitly says so.
    audience_total: numOrNull(res.audience_total),
    truncated: res.truncated === true,
    // Pinned to the sandbox worker — this result can NEVER be a real send.
    source: strOf(res.source, 'worker.sandbox'),
  };
}

/* ============================================================================
 * SEGMENTS — list (free read), create (write), preview (free read).
 * ========================================================================== */

// ---- GET /marketing/segments (free read, workspace-scoped) -----------------
export async function fetchSegments(): Promise<SegmentList> {
  if (USE_MOCK_DATA) return { segments: [...MOCK_SEGMENTS], source: 'bc_contacts+filters' };
  const res = await api.get<SegmentListDto>('/marketing/segments');
  return {
    segments: (res.segments ?? []).map(adaptSegment),
    source: strOf(res.source, 'bc_contacts+filters'),
  };
}

// ---- POST /marketing/segments (create; no send) ----------------------------
export async function createSegment(input: SegmentCreateInput): Promise<Segment> {
  if (USE_MOCK_DATA) {
    const created: Segment = {
      id: nextMockId('seg'),
      name: input.name,
      filters: input.filters,
      count: null, // honest: not previewed yet
      created_at: new Date().toISOString(),
      source: 'bc_contacts+filters',
    };
    MOCK_SEGMENTS.unshift(created);
    return created;
  }
  const res = await api.post<SegmentDto>('/marketing/segments', input);
  return adaptSegment(res);
}

// ---- GET /marketing/segments/{id}/preview (free read over contacts) --------
export async function fetchSegmentPreview(segmentId: string): Promise<SegmentPreview> {
  if (USE_MOCK_DATA) {
    const seg = MOCK_SEGMENTS.find((s) => s.id === segmentId);
    // A segment with a real 0/empty case stays honest (empty, not fabricated).
    const contacts = seg && seg.count === 0 ? [] : MOCK_PREVIEW_CONTACTS;
    return { contacts, count: contacts.length, source: 'bc_contacts+filters' };
  }
  const res = await api.get<SegmentPreviewDto>(`/marketing/segments/${segmentId}/preview`);
  const contacts = (res.contacts ?? []).map(adaptContact);
  return {
    contacts,
    count: intOf(res.count),
    source: strOf(res.source, 'bc_contacts+filters'),
  };
}

/* ============================================================================
 * CAMPAIGN ATTRIBUTION — free read; honest nulls (revenue null until Won).
 * ========================================================================== */

// ---- GET /marketing/campaigns/{id}/attribution (free read) -----------------
export async function fetchCampaignAttribution(campaignId: string): Promise<CampaignAttribution> {
  if (USE_MOCK_DATA) {
    return (
      MOCK_ATTRIBUTION[campaignId] ?? {
        sent: 0,
        opened: 0,
        clicked: 0,
        replied: 0,
        opportunities: 0,
        won_value: null,
        currency: 'GBP',
        revenue_per_recipient: null,
        note: ATTRIBUTION_NOTE,
        source: 'bksesh_sales_lead_events+bc_crm_opportunities',
      }
    );
  }
  const res = await api.get<CampaignAttributionDto>(`/marketing/campaigns/${campaignId}/attribution`);
  return {
    sent: intOf(res.sent),
    opened: intOf(res.opened),
    clicked: intOf(res.clicked),
    replied: intOf(res.replied),
    opportunities: intOf(res.opportunities),
    // Null-honest: revenue stays null until a Won opportunity — never £0.
    won_value: numOrNull(res.won_value),
    currency: strOf(res.currency, 'GBP'),
    revenue_per_recipient: numOrNull(res.revenue_per_recipient),
    note: strOf(res.note, ATTRIBUTION_NOTE),
    source: strOf(res.source, 'bksesh_sales_lead_events+bc_crm_opportunities'),
  };
}
